import { toKenyanMobile, type SmsIntegration, type SmsProviderSummary } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { toConfigPayload, type ConfigFormValues } from '../../../policies/config-form-values.policy';
import { changesCredentials } from '../../../policies/messaging-provider.policy';
import { getSmsIntegration, installSmsIntegration, sendSmsTest, updateSmsIntegration } from '../sms.api';

export interface SmsFormValues extends ConfigFormValues {
  displayName: string;
  testTo?: string;
}

interface UseSmsIntegrationSheetArgs {
  providers: SmsProviderSummary[];
  /** The provider being installed (`?provider=`), or undefined for settings. */
  providerKey: string | undefined;
  /** The installation whose settings are open, or undefined when installing. */
  integrationId: string | undefined;
  onDone: () => void;
}

type SavedState = { status: 'none' } | { status: 'loading' } | { status: 'loaded'; integration: SmsIntegration } | { status: 'error' };

/**
 * Install a provider or change its settings. Both send a real test text when the credentials are new, and the API
 * saves nothing if the provider refuses it, so the refusal shows inline (CLAUDE.md → Feedback).
 */
export function useSmsIntegrationSheet({ providers, providerKey, integrationId, onDone }: UseSmsIntegrationSheetArgs) {
  const toast = useToast();
  const [saved, setSaved] = useState<SavedState>(integrationId ? { status: 'loading' } : { status: 'none' });
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!integrationId) return;
    let cancelled = false;
    getSmsIntegration(integrationId)
      .then((integration) => !cancelled && setSaved({ status: 'loaded', integration }))
      .catch(() => !cancelled && setSaved({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  const integration = saved.status === 'loaded' ? saved.integration : undefined;
  const wantedKey = providerKey ?? integration?.providerKey;
  const provider = providers.find((candidate) => candidate.key === wantedKey);

  async function save(values: SmsFormValues): Promise<void> {
    if (!provider) return;
    setSubmitting(true);
    setError(null);
    const config = toConfigPayload(provider.fields, values);
    const testTo = values.testTo ? (toKenyanMobile(values.testTo) ?? values.testTo) : undefined;
    try {
      if (integration) {
        const isTested = changesCredentials(provider.fields, integration, values);
        await updateSmsIntegration(integration.id, { displayName: values.displayName, ...(isTested ? { config, testTo } : {}) });
        toast.success(`${values.displayName} saved`);
      } else {
        await installSmsIntegration({ providerKey: provider.key, displayName: values.displayName, config, testTo: testTo ?? '' });
        toast.success(`${values.displayName} installed and sending texts`);
      }
      onDone();
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  /** Settings only: a test text with the saved credentials. */
  async function sendTest(typed: string): Promise<void> {
    if (!integration) return;
    const to = toKenyanMobile(typed) ?? typed;
    setTesting(true);
    setError(null);
    try {
      setSaved({ status: 'loaded', integration: await sendSmsTest(integration.id, to) });
      toast.success(`Test text sent to ${to}`);
    } catch (err) {
      setError(apiErrorReason(err, 'The test text could not be sent'));
    } finally {
      setTesting(false);
    }
  }

  return { provider, integration, isLoading: saved.status === 'loading', loadFailed: saved.status === 'error', submitting, testing, error, save, sendTest };
}
